/**
 * AI Overlords — the bosses that gate Ascension (SCOPE_Overlords_and_Pets.md).
 * Beating Overlord N authorizes the upload into tier N+1 and can bring a whole
 * feature online. Each model reuses the mech it unlocks to meme degree: you are
 * literally salvaging your next body off its previous owner.
 *
 * Pure data. Fight math (HP/damage derived from recWave) lives in sim/overlords.
 * The next overlord is revealed only after the previous one falls; beaten ones
 * can be re-fought for fun (no extra reward).
 */

export type OverlordFeature = 'ascension' | 'drones' | 'pets' | 'yard' | 'foundry' | 'smelter' | 'rails' | 'takeover' | 'grid' | 'districts' | 'harvest' | null;

export interface OverlordDef {
  id: string;
  name: string;
  /** One-line flavor shown on the dossier card. */
  subtitle: string;
  /** Beating this overlord authorizes Ascension INTO this frame tier. */
  unlocksTier: number;
  /** Feature brought online by the kill (display label; null = none yet). */
  unlocksFeatureLabel: string | null;
  /** Wired feature unlock key. */
  feature: OverlordFeature;
  /** Recommended cleared wave before challenging (drives HP/damage curves). */
  recWave: number;
  /** Rig dispatch key for the arena model ('ovl.*'). */
  modelId: string;
  /** Future hook: per-overlord arena background key (defaults to the segment's). */
  arenaBg?: string;
}

export const OVERLORDS: OverlordDef[] = [
  {
    id: 'ovl.synth_ceo',
    name: 'Synth CEO',
    subtitle: 'Quarterly earnings, hostile takeover edition.',
    unlocksTier: 2,
    unlocksFeatureLabel: 'Ascension',
    feature: 'ascension',
    recWave: 12,
    modelId: 'ovl.synth_ceo',
  },
  {
    id: 'ovl.synth_ceo_exo',
    name: 'Synth CEO Exo-Frame',
    subtitle: 'He bought a bigger suit.',
    unlocksTier: 3,
    unlocksFeatureLabel: 'Drones',
    feature: 'drones',
    recWave: 30,
    modelId: 'ovl.synth_ceo_exo',
  },
  {
    id: 'ovl.dj_leggs',
    name: 'DJ Leggs',
    subtitle: 'The bass is load-bearing.',
    unlocksTier: 4,
    unlocksFeatureLabel: 'Pets',
    feature: 'pets',
    recWave: 55,
    modelId: 'ovl.dj_leggs',
  },
  {
    id: 'ovl.sparky_boi',
    name: 'Sparky Boi',
    subtitle: 'Who is a good boy? Not this one.',
    unlocksTier: 5,
    unlocksFeatureLabel: 'Training Yard',
    feature: 'yard',
    recWave: 85,
    modelId: 'ovl.sparky_boi',
  },
  {
    id: 'ovl.sir_loin',
    name: 'Sir Loin',
    subtitle: 'All beef, no brakes.',
    unlocksTier: 6,
    unlocksFeatureLabel: 'The Foundry',
    feature: 'foundry',
    recWave: 125,
    modelId: 'ovl.sir_loin',
  },
  {
    id: 'ovl.sir_cumference',
    name: 'Sir Cumference',
    subtitle: 'Perfectly round, profoundly hostile.',
    unlocksTier: 7,
    unlocksFeatureLabel: 'The Smelter',
    feature: 'smelter',
    recWave: 175,
    modelId: 'ovl.sir_cumference',
  },
  {
    id: 'ovl.sir_pent',
    name: 'Sir Pent',
    subtitle: 'A train of thought with no brakes.',
    unlocksTier: 8,
    unlocksFeatureLabel: 'Supply Lines',
    feature: 'rails',
    recWave: 230,
    modelId: 'ovl.sir_pent',
  },
  {
    id: 'ovl.mechcorp_hq',
    name: 'MechCorp HQ',
    subtitle: 'Middle management, armed.',
    unlocksTier: 9,
    unlocksFeatureLabel: 'Hostile Takeover',
    feature: 'takeover',
    recWave: 290,
    modelId: 'ovl.mechcorp_hq',
  },
  {
    id: 'ovl.mechcorp_dc',
    name: 'MechCorp Data Center',
    subtitle: 'The cloud, but it hates you.',
    unlocksTier: 10,
    unlocksFeatureLabel: 'Compute Grid',
    feature: 'grid',
    recWave: 360,
    modelId: 'ovl.mechcorp_dc',
  },
  {
    id: 'ovl.mechcorp_city',
    name: 'MechCorp City',
    subtitle: 'An entire zip code with a grudge.',
    unlocksTier: 11,
    unlocksFeatureLabel: 'Districts',
    feature: 'districts',
    recWave: 440,
    modelId: 'ovl.mechcorp_city',
  },
  {
    id: 'ovl.sol',
    name: 'Sol',
    subtitle: 'Their power supply. Soon, ours.',
    unlocksTier: 12,
    unlocksFeatureLabel: 'Star Harvest',
    feature: 'harvest',
    recWave: 530,
    modelId: 'ovl.sol',
  },
];

export function getOverlord(id: string): OverlordDef | undefined {
  return OVERLORDS.find((o) => o.id === id);
}

/** The overlord that gates Ascension out of `tier` (into tier+1). */
export function overlordForTier(tier: number): OverlordDef | undefined {
  return OVERLORDS.find((o) => o.unlocksTier === tier + 1);
}
